/* Sprint 2O-E — Vognmand Selvbetjenings Portal: Akkord Dashboard.
   Shows own data only: dashboard KPIs, akkord settlements with invoice/dispute workflow,
   own drivers/routes (phone masked, safe projection only). */
import React from "react";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import {
  Building2, LogOut, Download, FileSpreadsheet, AlertTriangle, CheckCircle2,
  Clock, Banknote, MessageSquare, ArrowLeft, RefreshCw, Loader2, TrendingUp,
  Users, Truck, X,
} from "lucide-react";
import { scApi } from "../lib/api";

const STATUS_META = {
  pending: { label: "Afventer", cls: "bg-slate-100 text-slate-700", icon: Clock },
  sent: { label: "Sendt", cls: "bg-blue-50 text-blue-800", icon: FileSpreadsheet },
  invoice_submitted: { label: "Faktura indsendt", cls: "bg-amber-50 text-amber-800", icon: CheckCircle2 },
  ready_for_payment: { label: "Klar til betaling", cls: "bg-indigo-50 text-indigo-800", icon: Banknote },
  paid: { label: "Betalt", cls: "bg-emerald-50 text-emerald-800", icon: Banknote },
  disputed: { label: "Bestridt", cls: "bg-red-50 text-red-800", icon: AlertTriangle },
};

export default function VognmandAkkordPage() {
  const navigate = useNavigate();
  const [tab, setTab] = React.useState("dashboard");
  const [dashboard, setDashboard] = React.useState(null);
  const [settlements, setSettlements] = React.useState([]);
  const [routes, setRoutes] = React.useState([]);
  const [drivers, setDrivers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [invoiceModal, setInvoiceModal] = React.useState(null);
  const [disputeModal, setDisputeModal] = React.useState(null);

  React.useEffect(() => {
    const token = localStorage.getItem("ema_sc_token");
    if (!token) {
      navigate("/vognmand/login");
    }
  }, [navigate]);

  const loadAll = React.useCallback(async () => {
    setLoading(true);
    try {
      const sc = scApi();
      const [d, s, r, dr] = await Promise.all([
        sc.get("/sc/dashboard"),
        sc.get("/sc/akkord-settlements?months=12"),
        sc.get("/sc/own-routes?limit=100"),
        sc.get("/sc/own-drivers-extended"),
      ]);
      setDashboard(d.data);
      setSettlements(s.data.items || []);
      setRoutes(r.data.items || []);
      setDrivers(dr.data.items || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate("/vognmand/login");
        return;
      }
      toast.error("Kunne ikke hente data");
    } finally { setLoading(false); }
  }, [navigate]);

  React.useEffect(() => { loadAll(); }, [loadAll]);

  const onDownload = async (period) => {
    try {
      const r = await scApi().get(`/sc/akkord-settlements/${period}/workbook.xlsx`,
                                  { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([r.data]));
      link.download = `EMA_Vognmandsafregning_${period}.xlsx`;
      link.click();
      toast.success("Workbook downloadet");
    } catch (e) { toast.error("Download fejlede"); }
  };

  const logout = () => {
    localStorage.removeItem("ema_sc_token");
    navigate("/vognmand/login");
  };

  if (loading && !dashboard) {
    return (
      <div className="min-h-screen bg-brand-bg2 flex items-center justify-center">
        <Loader2 className="animate-spin text-brand-red" size={32}/>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg2 pb-12" data-testid="vognmand-akkord-page">
      {/* Header */}
      <div className="bg-brand-dark text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/vognmand" className="text-white/80 hover:text-white flex items-center gap-1 text-sm"
            data-testid="back-to-portal">
            <ArrowLeft size={16}/> Tilbage til portal
          </Link>
          <div className="border-l border-white/30 pl-3 ml-1">
            <div className="font-display font-bold text-lg flex items-center gap-2">
              <Building2 size={20}/> Vagtplan & Akkord-afregning
            </div>
            <div className="text-xs text-white/60">
              {dashboard?.company?.name} {dashboard?.company?.cvr ? `· CVR ${dashboard.company.cvr}` : ""}
            </div>
          </div>
        </div>
        <button onClick={logout} data-testid="vognmand-logout"
          className="bg-brand-red hover:bg-brand-red/90 text-white px-3 py-2 text-xs font-bold tracking-wider uppercase flex items-center gap-1">
          <LogOut size={12}/> Log ud
        </button>
      </div>

      {/* KPI Strip */}
      {dashboard && (
        <div className="bg-white border-b border-brand px-6 py-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="vognmand-kpis">
            <Kpi icon={Truck} label="Ruter denne måned" value={dashboard.kpis.routes_this_month}/>
            <Kpi icon={Clock} label="Godkendte timer" value={dashboard.kpis.approved_hours_this_month?.toFixed(1) || "0.0"}/>
            <Kpi icon={Banknote} label="Total (DKK inkl. moms)" value={dashboard.kpis.settlement_total_inc_vat_this_month?.toFixed(2) || "—"}/>
            <Kpi icon={AlertTriangle} label="Åbne tvister" value={dashboard.kpis.open_disputes} color={dashboard.kpis.open_disputes > 0 ? "text-red-700" : "text-brand-dark"}/>
            <Kpi icon={FileSpreadsheet} label="Mangler fakturanr." value={dashboard.kpis.settlements_missing_invoice_number} color={dashboard.kpis.settlements_missing_invoice_number > 0 ? "text-amber-700" : "text-brand-dark"}/>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white border-b border-brand sticky top-0 z-10">
        <div className="flex">
          {[
            { k: "dashboard", t: "Dashboard" },
            { k: "settlements", t: "Afregninger" },
            { k: "routes", t: "Mine ruter" },
            { k: "drivers", t: "Mine chauffører" },
          ].map((t) => (
            <button
              key={t.k} onClick={() => setTab(t.k)}
              data-testid={`vognmand-tab-${t.k}`}
              className={`flex-1 py-3 text-sm font-bold uppercase tracking-wider ${tab === t.k ? "border-b-2 border-brand-red text-brand-red bg-brand-bg2" : "text-brand-text2"}`}
            >
              {t.t}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {tab === "dashboard" && dashboard && (
          <div className="space-y-4" data-testid="vognmand-dashboard-tab">
            <h2 className="font-display font-bold text-xl text-brand-dark">Status oversigt</h2>
            <div className="bg-white border border-brand p-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div><b>Selskab:</b> {dashboard.company.name}</div>
                <div><b>CVR:</b> {dashboard.company.cvr || "—"}</div>
                <div><b>Email:</b> {dashboard.company.email}</div>
              </div>
            </div>
            <h3 className="font-display font-bold text-lg text-brand-dark mt-6">Seneste afregningsstatus</h3>
            <div className="bg-white border border-brand">
              <table className="w-full text-sm" data-testid="vognmand-status-table">
                <thead className="bg-brand-bg2">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs uppercase">Periode</th>
                    <th className="px-3 py-2 text-left text-xs uppercase">Status</th>
                    <th className="px-3 py-2 text-left text-xs uppercase">Fakturanr.</th>
                    <th className="px-3 py-2 text-left text-xs uppercase">Bestridt</th>
                    <th className="px-3 py-2 text-left text-xs uppercase">Opdateret</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.recent_settlements_status.length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-brand-text2">Ingen status endnu</td></tr>}
                  {dashboard.recent_settlements_status.map((s) => (
                    <tr key={s.id || s.period} className="border-t border-brand">
                      <td className="px-3 py-2 font-bold">{s.period}</td>
                      <td className="px-3 py-2"><StatusBadge status={s.status}/></td>
                      <td className="px-3 py-2 font-mono text-xs">{s.invoice_number || "—"}</td>
                      <td className="px-3 py-2">{s.disputed ? <span className="text-red-700 font-bold">Ja</span> : "Nej"}</td>
                      <td className="px-3 py-2 text-xs">{(s.updated_at || "").slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "settlements" && (
          <div className="space-y-3" data-testid="vognmand-settlements-tab">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-xl text-brand-dark">Månedlige akkord-afregninger</h2>
              <button onClick={loadAll} className="bg-brand-bg2 px-3 py-2 text-xs border border-brand flex items-center gap-1">
                <RefreshCw size={12}/> Opdatér
              </button>
            </div>
            <div className="bg-white border border-brand overflow-x-auto">
              <table className="w-full text-sm" data-testid="vognmand-settlements-table">
                <thead className="bg-brand-bg2">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs uppercase">Periode</th>
                    <th className="px-3 py-2 text-left text-xs uppercase">Status</th>
                    <th className="px-3 py-2 text-left text-xs uppercase">Ruter</th>
                    <th className="px-3 py-2 text-left text-xs uppercase">Ex moms</th>
                    <th className="px-3 py-2 text-left text-xs uppercase">Inkl. moms</th>
                    <th className="px-3 py-2 text-left text-xs uppercase">Fakturanr.</th>
                    <th className="px-3 py-2 text-left text-xs uppercase">Sendt</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {settlements.map((s) => (
                    <tr key={s.period} className="border-t border-brand hover:bg-brand-bg2/30"
                      data-testid={`settlement-row-${s.period}`}>
                      <td className="px-3 py-2 font-bold">{s.period}</td>
                      <td className="px-3 py-2"><StatusBadge status={s.status}/></td>
                      <td className="px-3 py-2">{s.route_count || "—"}</td>
                      <td className="px-3 py-2 font-mono">{s.total_ex_vat ? `${s.total_ex_vat.toFixed(2)}` : "—"}</td>
                      <td className="px-3 py-2 font-mono font-bold">{s.total_inc_vat ? `${s.total_inc_vat.toFixed(2)}` : "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs">{s.invoice_number || <span className="text-amber-700">—</span>}</td>
                      <td className="px-3 py-2 text-xs">{(s.sent_at || "").slice(0, 10) || "—"}</td>
                      <td className="px-3 py-2 flex gap-1">
                        <button onClick={() => onDownload(s.period)}
                          data-testid={`download-${s.period}`}
                          className="bg-emerald-700 text-white px-2 py-1 text-[10px] font-bold uppercase flex items-center gap-1">
                          <Download size={10}/>
                        </button>
                        <button onClick={() => setInvoiceModal(s)}
                          data-testid={`invoice-${s.period}`}
                          className="bg-blue-700 text-white px-2 py-1 text-[10px] font-bold uppercase">
                          Faktura
                        </button>
                        <button onClick={() => setDisputeModal(s)}
                          data-testid={`dispute-${s.period}`}
                          className="bg-red-700 text-white px-2 py-1 text-[10px] font-bold uppercase">
                          Bestrid
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "routes" && (
          <div data-testid="vognmand-routes-tab">
            <h2 className="font-display font-bold text-xl text-brand-dark mb-4">Mine ruter (seneste 100)</h2>
            <div className="bg-white border border-brand overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-brand-bg2 sticky top-0">
                  <tr>
                    <th className="px-2 py-2 text-left uppercase">Dato</th>
                    <th className="px-2 py-2 text-left uppercase">Rute-ID</th>
                    <th className="px-2 py-2 text-left uppercase">Chauffør</th>
                    <th className="px-2 py-2 text-left uppercase">Tid</th>
                    <th className="px-2 py-2 text-left uppercase">Algo (min)</th>
                    <th className="px-2 py-2 text-left uppercase">Akkord (min)</th>
                    <th className="px-2 py-2 text-left uppercase">Godkendt (t)</th>
                    <th className="px-2 py-2 text-left uppercase">Stop ✓/✗</th>
                  </tr>
                </thead>
                <tbody>
                  {routes.length === 0 && <tr><td colSpan={8} className="px-2 py-4 text-center text-brand-text2">Ingen ruter</td></tr>}
                  {routes.map((r) => (
                    <tr key={r.id} className="border-t border-brand">
                      <td className="px-2 py-1">{r.date}</td>
                      <td className="px-2 py-1 font-mono text-[10px]">{r.route_number}</td>
                      <td className="px-2 py-1">{r.driver_name}</td>
                      <td className="px-2 py-1">{r.planned_start && r.planned_end ? `${r.planned_start}-${r.planned_end}` : "—"}</td>
                      <td className="px-2 py-1">{r.algorithm_duration_minutes || "—"}</td>
                      <td className="px-2 py-1">{r.akkord_payable_minutes || "—"}</td>
                      <td className="px-2 py-1 font-bold">{r.approved_payable_hours?.toFixed(2) || "—"}</td>
                      <td className="px-2 py-1">{r.completed_stops}/{r.failed_stops}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "drivers" && (
          <div data-testid="vognmand-drivers-tab">
            <h2 className="font-display font-bold text-xl text-brand-dark mb-4">Mine chauffører</h2>
            <div className="bg-white border border-brand overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-brand-bg2">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs uppercase">Navn</th>
                    <th className="px-3 py-2 text-left text-xs uppercase">Email</th>
                    <th className="px-3 py-2 text-left text-xs uppercase">Telefon</th>
                    <th className="px-3 py-2 text-left text-xs uppercase">Aktiv</th>
                    <th className="px-3 py-2 text-left text-xs uppercase">Køretøj</th>
                    <th className="px-3 py-2 text-left text-xs uppercase">Ruter (måned)</th>
                    <th className="px-3 py-2 text-left text-xs uppercase">Godkendte timer</th>
                    <th className="px-3 py-2 text-left text-xs uppercase">Seneste rute</th>
                  </tr>
                </thead>
                <tbody>
                  {drivers.length === 0 && <tr><td colSpan={8} className="px-3 py-4 text-center text-brand-text2">Ingen chauffører</td></tr>}
                  {drivers.map((d) => (
                    <tr key={d.id} className="border-t border-brand">
                      <td className="px-3 py-2">{d.name}</td>
                      <td className="px-3 py-2 text-xs">{d.email}</td>
                      <td className="px-3 py-2"><code className="text-xs">{d.phone_masked}</code></td>
                      <td className="px-3 py-2">{d.active !== false ? "✓" : "✗"}</td>
                      <td className="px-3 py-2 text-xs">{d.vehicle_type || "—"}</td>
                      <td className="px-3 py-2">{d.routes_this_month}</td>
                      <td className="px-3 py-2 font-bold">{d.approved_hours_this_month?.toFixed(1)}</td>
                      <td className="px-3 py-2 text-xs">{d.last_route_date || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {invoiceModal && (
        <InvoiceModal settlement={invoiceModal} onClose={() => setInvoiceModal(null)}
          onSubmitted={() => { setInvoiceModal(null); loadAll(); }}/>
      )}
      {disputeModal && (
        <DisputeModal settlement={disputeModal} onClose={() => setDisputeModal(null)}
          onSubmitted={() => { setDisputeModal(null); loadAll(); }}/>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, color = "text-brand-dark" }) {
  return (
    <div className="bg-brand-bg2 border border-brand p-3">
      <div className="text-[10px] uppercase tracking-wider text-brand-text2 flex items-center gap-1">
        <Icon size={10}/> {label}
      </div>
      <div className={`font-display font-bold text-xl mt-1 ${color}`}>{value ?? "—"}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${m.cls}`}>
      <Icon size={10}/> {m.label}
    </span>
  );
}

function InvoiceModal({ settlement, onClose, onSubmitted }) {
  const [invoiceNumber, setInvoiceNumber] = React.useState(settlement.invoice_number || "");
  const [invoiceDate, setInvoiceDate] = React.useState("");
  const [comment, setComment] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const onSubmit = async () => {
    if (!invoiceNumber.trim()) { toast.error("Fakturanummer er påkrævet"); return; }
    setSaving(true);
    try {
      await scApi().post(`/sc/akkord-settlements/${settlement.period}/invoice`, {
        invoice_number: invoiceNumber.trim(),
        invoice_date: invoiceDate || null,
        comment: comment || null,
      });
      toast.success("Faktura indsendt - admin er informeret");
      onSubmitted();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke gemme"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="invoice-modal">
      <div className="bg-white border border-brand max-w-md w-full">
        <div className="bg-brand-dark text-white px-5 py-4 flex items-center justify-between">
          <div className="font-display font-bold">Indsend faktura — {settlement.period}</div>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-wider font-bold mb-1">Fakturanummer *</label>
            <input type="text" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)}
              data-testid="invoice-number-input"
              className="w-full border border-brand p-2 text-sm"/>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider font-bold mb-1">Fakturadato</label>
            <input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)}
              data-testid="invoice-date-input"
              className="w-full border border-brand p-2 text-sm"/>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider font-bold mb-1">Kommentar (valgfri)</label>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)}
              rows={3} data-testid="invoice-comment-input"
              className="w-full border border-brand p-2 text-sm"/>
          </div>
          <p className="text-xs text-brand-text2">
            Når du indsender, opdateres status til "Faktura indsendt" og EMA admin underrettes.
          </p>
        </div>
        <div className="border-t border-brand p-4 flex justify-end gap-3 bg-brand-bg2">
          <button onClick={onClose} className="px-4 py-2 text-sm">Annullér</button>
          <button onClick={onSubmit} disabled={saving || !invoiceNumber.trim()}
            data-testid="invoice-submit-btn"
            className="bg-brand-red text-white px-5 py-2 text-sm font-bold tracking-wider uppercase disabled:opacity-50">
            {saving ? "Indsender..." : "Indsend faktura"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DisputeModal({ settlement, onClose, onSubmitted }) {
  const [reason, setReason] = React.useState("");
  const [lineRef, setLineRef] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const onSubmit = async () => {
    if (!reason.trim()) { toast.error("Begrundelse er påkrævet"); return; }
    setSaving(true);
    try {
      await scApi().post(`/sc/akkord-settlements/${settlement.period}/dispute`, {
        reason: reason.trim(), line_reference: lineRef || null,
      });
      toast.success("Bestridelse registreret - admin vil følge op");
      onSubmitted();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke gemme"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="dispute-modal">
      <div className="bg-white border border-brand max-w-md w-full">
        <div className="bg-red-700 text-white px-5 py-4 flex items-center justify-between">
          <div className="font-display font-bold flex items-center gap-2">
            <AlertTriangle size={18}/> Bestrid afregning — {settlement.period}
          </div>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-wider font-bold mb-1">Begrundelse *</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)}
              rows={4} placeholder="Beskriv hvad der er forkert..."
              data-testid="dispute-reason-input"
              className="w-full border border-brand p-2 text-sm"/>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider font-bold mb-1">Linje-reference (valgfri)</label>
            <input type="text" value={lineRef} onChange={(e) => setLineRef(e.target.value)}
              placeholder="fx 'rute R-12345' eller 'linje 8'"
              data-testid="dispute-line-input"
              className="w-full border border-brand p-2 text-sm"/>
          </div>
          <p className="text-xs text-amber-700">
            ⚠ Bestridelse pauser betaling indtil admin har gennemgået og løst sagen.
          </p>
        </div>
        <div className="border-t border-brand p-4 flex justify-end gap-3 bg-brand-bg2">
          <button onClick={onClose} className="px-4 py-2 text-sm">Annullér</button>
          <button onClick={onSubmit} disabled={saving || !reason.trim()}
            data-testid="dispute-submit-btn"
            className="bg-red-700 text-white px-5 py-2 text-sm font-bold tracking-wider uppercase disabled:opacity-50">
            {saving ? "Indsender..." : "Indsend bestridelse"}
          </button>
        </div>
      </div>
    </div>
  );
}
